/**
 * Assessment Service
 * Handles impact assessment API calls
 */

import { apiClient, type ApiResponse } from "./apiClient";

export type AssessmentTaskStatus =
  | "init"
  | "running"
  | "success"
  | "failed"
  | "partial";

export type AssessmentBy = "user" | "system";

/** values/search API response item */
interface ConceptsValuesSearchItem {
  concept: { code: string; name: string; description?: string };
  values: Array<{ code: string; name: string; description?: string | null }>;
}

function safeRecordSet(
  record: Record<string, string>,
  key: unknown,
  value: unknown,
): void {
  if (typeof key !== "string" || !key.trim()) return;
  if (typeof value !== "string" || !value.trim()) return;
  record[key] = value;
}

async function getConceptAndValueDescriptionsFromSearch(): Promise<{
  conceptCodeToDesc: Record<string, string>;
  valueCodeToDesc: Record<string, string>;
}> {
  const conceptCodeToDesc: Record<string, string> = {};
  const valueCodeToDesc: Record<string, string> = {};
  try {
    const result = await apiClient.get<ConceptsValuesSearchItem[]>(
      "/knowledge_manager/concepts/values/user-search",
      { page_size: 20, page: 1 },
    );
    const rawData = result.data ?? [];
    if (Array.isArray(rawData)) {
      for (const item of rawData) {
        safeRecordSet(
          conceptCodeToDesc,
          item.concept?.code,
          item.concept?.description,
        );
        for (const v of item.values ?? []) {
          safeRecordSet(valueCodeToDesc, v.code, v.description ?? v.name);
        }
      }
    }
  } catch (error) {
    console.error("Failed to fetch concept/value descriptions:", error);
  }
  return { conceptCodeToDesc, valueCodeToDesc };
}

/**
 * Document interface
 */
export interface AssessmentDocument {
  file_path: string;
  name: string;
  guid: string;
}

/**
 * Regulation for assessment
 */
export interface AssessmentRegulation {
  id: string;
  version: string;
  title: string;
  documents: AssessmentDocument[];
}

/**
 * Concept value for assessment
 */
export interface AssessmentConceptValue {
  name: string;
  code?: string;
  description: string;
}

/**
 * Concept for assessment
 */
export interface AssessmentConcept {
  concept: {
    name: string;
    code: string;
    description: string;
  };
  values: AssessmentConceptValue[];
}

/**
 * Assessment request payload
 */
export interface AssessmentRequest {
  regulations: AssessmentRegulation[];
  concepts: AssessmentConcept[];
  keywords: string[];
  by?: AssessmentBy;
}

/**
 * Assessment response
 */
export interface AssessmentBundleTaskData {
  /** System bundle API may return `task_id` (e.g. bt-…); poll body uses `task_uuid`. */
  task_id?: string;
  task_uuid?: string;
  bundle_task_uuid?: string;
  taskUuid?: string;
  status?: AssessmentTaskStatus;
  [key: string]: unknown;
}

export type AssessmentResponse = ApiResponse<AssessmentBundleTaskData>;

export interface AssessmentBundleTaskStatusData {
  task_uuid?: string;
  bundle_task_uuid?: string;
  /** Bundle-level status (some endpoints). */
  status?: AssessmentTaskStatus;
  /** Per-regulation rows from `POST /assessment/regulation-assessment-status`. */
  items?: unknown;
  results?: unknown;
  regulations?: unknown;
  [key: string]: unknown;
}

export type AssessmentBundleTaskStatusResponse =
  ApiResponse<AssessmentBundleTaskStatusData>;

export interface AssessmentReportPresignedUrl {
  presigned_url: string;
  filename?: string;
}

function extractTaskUuidFromData(
  data: AssessmentBundleTaskData | null | undefined,
): string | null {
  const value =
    data?.task_uuid ??
    data?.task_id ??
    data?.bundle_task_uuid ??
    data?.taskUuid;
  return typeof value === "string" && value.trim() ? value : null;
}

export function getAssessmentTaskUuid(response: AssessmentResponse): string | null {
  return extractTaskUuidFromData(response.data);
}

/**
 * Submit bundle task for assessment.
 * Before POST, fetches concept/value descriptions from values/search and enriches the payload.
 */
export async function submitBundleTask(
  payload: AssessmentRequest,
): Promise<AssessmentResponse> {
  console.log("\n=== API CALL ===");
  console.log("URL:", apiClient.getBaseURL() + "/assessment/bundle-task");

  try {
    const { conceptCodeToDesc, valueCodeToDesc } =
      await getConceptAndValueDescriptionsFromSearch();

    const enrichedConcepts: AssessmentConcept[] = payload.concepts.map((c) => ({
      concept: {
        name: c.concept.name,
        code: c.concept.code,
        description:
          conceptCodeToDesc[c.concept.code] ??
          c.concept.description ??
          c.concept.name,
      },
      values: c.values.map((v) => ({
        name: v.name,
        code: v.code,
        description:
          (v.code ? valueCodeToDesc[v.code] : undefined) ??
          v.description ??
          v.name,
      })),
    }));

    const enrichedPayload: AssessmentRequest = {
      regulations: payload.regulations,
      concepts: enrichedConcepts,
      keywords: payload.keywords,
      by: payload.by ?? "user",
    };

    console.log("Request payload:", JSON.stringify(enrichedPayload, null, 2));

    const result = await apiClient.post<AssessmentBundleTaskData>(
      "/assessment/bundle-task",
      enrichedPayload,
    );

    console.log("\n=== API RESPONSE ===");
    console.log("✅ Success:", result);

    return {
      status: result.status || "success",
      code: result.code || 200,
      message: result.message || undefined,
      data: result.data,
    };
  } catch (error) {
    console.error("\n❌ Exception:", error);
    if (error instanceof Error) {
      console.error("Error message:", error.message);
      console.error("Error stack:", error.stack);
    }
    // Error is already formatted by apiClient
    throw error;
  }
}

export async function getBundleTaskStatus(
  taskUuid: string,
): Promise<AssessmentBundleTaskStatusResponse> {
  const result = await apiClient.get<AssessmentBundleTaskStatusData>(
    `/assessment/bundle-tasks/${taskUuid}`,
  );

  return {
    status: result.status || "success",
    code: result.code || 200,
    message: result.message || undefined,
    data: result.data,
  };
}

export async function getAssessmentReportPresignedUrl(
  filePath: string,
): Promise<AssessmentReportPresignedUrl> {
  const response = await apiClient.post<AssessmentReportPresignedUrl>(
    "/assessment/reports/user-presigned-url",
    {
      file_path: filePath,
    },
  );

  if (response.status === "success" && response.data?.presigned_url) {
    return response.data;
  }

  throw new Error(response.message || "Failed to get presigned URL");
}

export async function rerunAssessment(params: {
  task_uuid: string;
  regulation_id: string;
  regulation_version: string;
}): Promise<
  ApiResponse<{
    task_id?: string;
    status?: string;
    sub_task_ids?: string[];
    result?: unknown;
  }>
> {
  return await apiClient.post("/assessment/rerun", params);
}

/** Re-run for system-configured IA runs (assessment type `system`). */
export async function rerunSystemAssessment(params: {
  task_uuid: string;
  regulation_id: string;
  regulation_version: string;
}): Promise<
  ApiResponse<{
    task_id?: string;
    status?: string;
    sub_task_ids?: string[];
    result?: unknown;
  }>
> {
  return await apiClient.post("/assessment/rerun-system", params);
}

/**
 * Labels from Assessment Center `by` / GET `/assessment/histories` filter `by`.
 * Used to route rerun: {@link rerunSystemAssessment} vs {@link rerunAssessment}.
 */
export type AiImpactAssessmentScopeKind =
  | "system"
  | "company_scope"
  | "custom_scope"
  | "other";

/**
 * Classifies assessment scope for re-run. System bundle uses `/assessment/rerun-system`;
 * Company Scope and Custom Scope use `/assessment/rerun` with the same payload (non-system bundle).
 */
export function classifyAiImpactAssessmentScope(
  assessmentType: string,
): AiImpactAssessmentScopeKind {
  const t = assessmentType.trim().toLowerCase();
  if (t === "system") return "system";
  if (t === "company scope") return "company_scope";
  if (t === "custom scope") return "custom_scope";
  return "other";
}

/**
 * Assessment checklist create request
 */
export interface CreateAssessmentChecklistRequest {
  name: string;
  description: string;
  concepts: {
    concepts: Array<{
      concept_type: string;
      concept_code: string;
      values: Array<{ code: string; name: string }>;
    }>;
  };
  keywords: string[];
  is_favorite?: boolean;
}

/**
 * Assessment checklist concept value from API
 */
export interface AssessmentChecklistConceptValue {
  code: string;
  name: string;
}

/**
 * Assessment checklist concept from API
 */
export interface AssessmentChecklistConcept {
  concept_type: string;
  concept_code: string;
  values: AssessmentChecklistConceptValue[];
}

/**
 * Assessment checklist from API
 */
export interface AssessmentChecklistItem {
  id: number;
  name: string;
  description: string;
  concepts: {
    version: string;
    concepts: AssessmentChecklistConcept[];
  };
  keywords: string[];
  is_favorite?: boolean;
  favorited_at?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Assessment checklists list response
 */
export interface AssessmentChecklistsResponse {
  checklists: AssessmentChecklistItem[];
  total: number;
}

function normalizeChecklistsResponse(
  data: AssessmentChecklistsResponse | null | undefined,
): AssessmentChecklistsResponse {
  const checklists = Array.isArray(data?.checklists) ? data.checklists : [];
  return {
    checklists,
    total: typeof data?.total === "number" ? data.total : checklists.length,
  };
}

/**
 * Fetch assessment checklists list.
 * Failures return an empty list so a checklist outage cannot block other page APIs.
 */
export async function fetchAssessmentChecklists(): Promise<AssessmentChecklistsResponse> {
  try {
    const result =
      await apiClient.get<AssessmentChecklistsResponse>("/assessment-checklists");

    if (result.status === "success" && result.data) {
      return normalizeChecklistsResponse(result.data);
    }
    return { checklists: [], total: 0 };
  } catch (error) {
    console.error("Failed to fetch assessment checklists:", error);
    return { checklists: [], total: 0 };
  }
}

/**
 * Fetch a single assessment checklist by id (full detail including concepts values).
 * Use when editing so Selected Concepts can be restored.
 */
export async function fetchAssessmentChecklistById(
  id: number,
): Promise<AssessmentChecklistItem | null> {
  try {
    const url = "/assessment-checklists/" + id;
    const result = await apiClient.get<AssessmentChecklistItem>(url);

    if (result.status === "success" && result.data) {
      return result.data;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Update assessment checklist (edit / toggle favorite)
 */
export interface UpdateAssessmentChecklistRequest {
  name: string;
  description: string;
  concepts: {
    concepts: Array<{
      concept_type: string;
      concept_code: string;
      values: Array<{ code: string; name: string }>;
    }>;
  };
  keywords: string[];
  is_favorite?: boolean;
}

export async function updateAssessmentChecklist(
  id: number,
  payload: UpdateAssessmentChecklistRequest,
): Promise<AssessmentResponse> {
  try {
    const url = "/assessment-checklists/" + id;
    const result = await apiClient.put(url, payload);
    return {
      status: result.status || "success",
      code: result.code || 200,
      message: result.message || undefined,
      data: result.data,
    };
  } catch (error) {
    console.error("Failed to update assessment checklist:", error);
    throw error;
  }
}

/**
 * Delete assessment checklist
 */
export async function deleteAssessmentChecklist(
  id: number,
): Promise<AssessmentResponse> {
  try {
    const url = "/assessment-checklists/" + id;
    const result = await apiClient.delete(url);
    return {
      status: result.status || "success",
      code: result.code || 200,
      message: result.message || undefined,
      data: result.data,
    };
  } catch (error) {
    console.error("Failed to delete assessment checklist:", error);
    throw error;
  }
}

/**
 * Create assessment checklist
 */
export async function createAssessmentChecklist(
  payload: CreateAssessmentChecklistRequest,
): Promise<AssessmentResponse> {
  try {
    const result = await apiClient.post("/assessment-checklists", payload);
    return {
      status: result.status || "success",
      code: result.code || 200,
      message: result.message || undefined,
      data: result.data,
    };
  } catch (error) {
    console.error("Failed to create assessment checklist:", error);
    throw error;
  }
}

/**
 * Fetch concept values with full details
 */
/** Company-Scope system bundle preview (`POST /assessment/system-bundle-task/preview`). */
export interface SystemBundleTaskPreviewData {
  already_run_regulations?: AssessmentRegulation[];
  running_regulations?: AssessmentRegulation[];
  [key: string]: unknown;
}

export async function postSystemBundleTaskPreview(payload: {
  regulations: AssessmentRegulation[];
  by?: AssessmentBy;
}): Promise<ApiResponse<SystemBundleTaskPreviewData>> {
  const body = { ...payload, by: payload.by ?? "user" };
  const result = await apiClient.post<SystemBundleTaskPreviewData>(
    "/assessment/system-bundle-task/preview",
    body,
  );
  return {
    status: result.status || "success",
    code: result.code || 200,
    message: result.message || undefined,
    data: result.data,
  };
}

export async function postSystemBundleTaskCompanyScope(payload: {
  regulations: AssessmentRegulation[];
  by?: AssessmentBy;
}): Promise<AssessmentResponse> {
  const body = { ...payload, by: payload.by ?? "user" };
  const result = await apiClient.post<AssessmentBundleTaskData>(
    "/assessment/system-bundle-task",
    body,
  );
  return {
    status: result.status || "success",
    code: result.code || 200,
    message: result.message || undefined,
    data: result.data,
  };
}

export async function postRegulationAssessmentStatus(payload: {
  task_uuid: string;
  by: AssessmentBy;
  regulations: AssessmentRegulation[];
}): Promise<AssessmentBundleTaskStatusResponse> {
  const result = await apiClient.post<AssessmentBundleTaskStatusData>(
    "/assessment/regulation-assessment-status",
    payload,
  );
  return {
    status: result.status || "success",
    code: result.code || 200,
    message: result.message || undefined,
    data: result.data,
  };
}

export async function fetchConceptValuesWithDetails(
  conceptName: string,
  options?: { limit?: number },
): Promise<AssessmentConceptValue[]> {
  try {
    const params: Record<string, any> = {
      concept_name: conceptName,
    };

    // Only add limit if explicitly provided
    // When limit is not provided, backend returns all values (same as dropdown behavior)
    if (options?.limit) {
      params.limit = options.limit;
    }

    const result = await apiClient.get<AssessmentConceptValue[]>(
      "/knowledge_manager/concepts/values",
      params,
    );

    if (result.status === "success" && result.data) {
      return result.data;
    }
    return [];
  } catch (error) {
    console.error("Failed to fetch concept values for " + conceptName + ":", error);
    return [];
  }
}
